<template>
  <div
    class="collection-slide"
    data-analytics-config="exclude"
  >
    <cdr-link
      :href="link"
      class="collection-slide__image"
      @click="handleClick"
    >
      <cdr-img
        :alt="alt"
        :src="thumb"
        v-bind="lazyLoadProp"
      />
    </cdr-link>
    <cdr-text
      tag="h3"
      data-analytics-config="exclude"
    >
      <cdr-link
        :href="link"
        class="collection-slide__heading"
        modifier="standalone"
        @click="handleClick"
      >
        <cdr-text
          tag="span"
          class="collection-slide__brand"
        >
          {{ brand }}
        </cdr-text>
        <cdr-text
          tag="span"
          class="collection-slide__title"
        >
          {{ title }}
        </cdr-text>
      </cdr-link>
    </cdr-text>
    <cdr-text
      v-if="salePrice"
      data-ui="sale-price"
      class="collection-slide__sale-price"
      tag="span"
    >
      {{ salePrice }}
    </cdr-text>
    <span
      v-if="salePrice"
      class="sr-only"
    >
      compared to &nbsp;
    </span>
    <cdr-text
      class="collection-slide__price"
      :class="{'collection-slide__price--on-sale': salePrice}"
      tag="span"
    >
      {{ price }}
    </cdr-text>
  </div>
</template>

<script>
import { CdrImg, CdrLink, CdrText } from '@rei/cedar';
import { metricsLink } from '../../../utils/analytics';

export default {
  name: 'CollectionProduct',
  components: {
    CdrImg,
    CdrLink,
    CdrText,
  },
  props: {
    brand: {
      type: String,
      required: false,
      default: '',
    },
    heading: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
    link: {
      type: String,
      required: true,
    },
    price: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    thumb: {
      type: String,
      required: true,
    },
    salePrice: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    alt() {
      return `${this.brand} ${this.title}`;
    },
    linkName() {
      const { heading, index, title } = this;
      // 4_Early access_Outpulse Mid-GTX - Women's
      return `${index}_${heading}_${title}`;
    },
    lazyLoadProp() {
      const { index, lazyLoad } = this;
      const loadingLazy = { loading: 'lazy' };
      // respect lazyLoad override prop for first 4 images, always lazy-load off-screen images
      if (lazyLoad || index > 3) return loadingLazy;
      return {};
    },
  },
  methods: {
    /* c8 ignore start */
    handleClick() {
      metricsLink(this.linkName);
    },
    /* c8 ignore end */
  },
};
</script>

<style lang="scss">

.collection-slide {
  width: 232px;

  &:last-child {
    padding-right: $cdr-space-one-x;
  }

  h3 {
    margin-bottom: 0;
  }

  @include cdr-md-mq-up {
    width: initial;
    // all elements in carousel slides are last-child so this applies to all
    &:last-child {
      padding: $cdr-space-eighth-x $cdr-space-one-x 0;
    }
  }
}

.collection-slide__image.collection-slide__image {
  height: 267px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $cdr-color-background-secondary;
  margin-bottom: $cdr-space-one-x;
  border-radius: ($cdr-radius-softer * 2); // 0.8rem ain't a supported radius

  img {
    max-width: 184px;
    mix-blend-mode: darken;
  }
}

.collection-slide__link {
  color: $cdr-color-text-primary;
  &:hover {
    text-decoration: none;
  }
}

.collection-slide__brand, .collection-slide__title {
  display: block;
}

.collection-slide__brand, .collection-slide__title {
  margin-bottom: $cdr-space-quarter-x;
}

.collection-slide__brand {
  @include cdr-text-utility-sans-200;
}

.collection-slide__heading.collection-slide__heading {
  color: $cdr-color-text-primary;
  display: block;
}

.collection-slide__title {
  @include cdr-text-subheading-sans-400;
  color: $cdr-color-text-emphasis;
}

.collection-slide__price {
  @include cdr-text-body-300;
  &--on-sale{
    text-decoration: line-through;
  }
}

.collection-slide__sale-price{
  @include cdr-text-body-300;
  color: $cdr-color-text-sale;
  margin-right: 5px;
}
</style>
